import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TopMenuBar } from './components/TopMenuBar';
import { Toolbar } from './components/Toolbar';
import { BrowserPanel } from './components/BrowserPanel';
import { TransportControls } from './components/TransportControls';
import { PatternSequencer } from './components/PatternSequencer';
import { PianoRoll } from './components/PianoRoll';
import { Playlist } from './components/Playlist';
import { Mixer } from './components/Mixer';
import { EffectsRack } from './components/EffectsRack';
import { AdvancedFeaturesPanel } from './components/AdvancedFeaturesPanel';
import { useStudioStore } from './store/useStudioStore';
import { useDAWStore } from './store/daw-store';
import { useTempoAutomation } from './hooks/useTempoAutomation';
import { usePreferencesSync } from './hooks/usePreferencesSync';
import { useAutoSave } from './hooks/useAutoSave';
import ErrorTest from './components/ErrorTest';

// Animation variants for view transitions (Requirements 14.4, 14.5)
// These will be disabled when reduceMotion is enabled
const getViewVariants = (reduceMotion: boolean) => ({
  initial: {
    opacity: reduceMotion ? 1 : 0,
    x: reduceMotion ? 0 : 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: reduceMotion ? 0 : 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: reduceMotion ? 1 : 0,
    x: reduceMotion ? 0 : -20,
    transition: {
      duration: reduceMotion ? 0 : 0.2,
      ease: 'easeIn',
    },
  },
});

function App() {
  const activeView = useStudioStore((state) => state.activeView);
  const showAdvancedFeaturesPanel = useStudioStore((state) => state.showAdvancedFeaturesPanel);
  const toggleAdvancedFeaturesPanel = useStudioStore((state) => state.toggleAdvancedFeaturesPanel);
  const closeAdvancedFeaturesPanel = useStudioStore((state) => state.closeAdvancedFeaturesPanel);
  const reduceMotion = useDAWStore((state) => state.reduceMotion);
  
  const [showEffectsRack, setShowEffectsRack] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Get animation variants based on reduce motion setting (Requirements 14.4, 14.5)
  const viewVariants = getViewVariants(reduceMotion);

  // Apply tempo automation to transport during playback
  useTempoAutomation();
  
  // Load preferences from backend when authenticated (Requirements 9.3, 9.5)
  usePreferencesSync();
  
  // Enable auto-save functionality (Requirements 14.1, 14.2, 14.3, 14.4, 14.5)
  useAutoSave();

  // Detect screen size and update responsive state
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-hide effects rack on mobile/tablet
  useEffect(() => {
    if (isMobile || isTablet) {
      setShowEffectsRack(false);
    }
  }, [isMobile, isTablet]);

  // Keyboard shortcuts for advanced features (Requirements 15.1-15.5)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      
      // Cmd+Shift+A (Mac) or Ctrl+Shift+A (Windows/Linux) - Toggle Advanced Features Panel
      if (modKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        toggleAdvancedFeaturesPanel();
      }
      
      // Cmd+R (Mac) or Ctrl+R (Windows/Linux) - Toggle recording mode (Requirement 15.1)
      if (modKey && !e.shiftKey && e.key === 'r') {
        e.preventDefault();
        const { isRecording, setIsRecording } = useStudioStore.getState();
        setIsRecording(!isRecording);
      }
      
      // Cmd+M (Mac) or Ctrl+M (Windows/Linux) - Open MIDI Learn panel (Requirement 15.2)
      if (modKey && !e.shiftKey && e.key === 'm') {
        e.preventDefault();
        const { openAdvancedFeaturesPanel } = useStudioStore.getState();
        openAdvancedFeaturesPanel();
        // Set active tab to 'midi' after panel opens
        setTimeout(() => {
          const tabTrigger = document.querySelector('[data-value="midi"]') as HTMLElement;
          if (tabTrigger) tabTrigger.click();
        }, 100);
      }
      
      // Cmd+T (Mac) or Ctrl+T (Windows/Linux) - Open time stretch editor (Requirement 15.3)
      if (modKey && !e.shiftKey && e.key === 't') {
        e.preventDefault();
        const { openAdvancedFeaturesPanel } = useStudioStore.getState();
        openAdvancedFeaturesPanel();
        // Set active tab to 'audio' after panel opens
        setTimeout(() => {
          const tabTrigger = document.querySelector('[data-value="audio"]') as HTMLElement;
          if (tabTrigger) tabTrigger.click();
        }, 100);
      }
      
      // Cmd+Shift+E (Mac) or Ctrl+Shift+E (Windows/Linux) - Open stem export dialog (Requirement 15.4)
      if (modKey && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        const { openAdvancedFeaturesPanel } = useStudioStore.getState();
        openAdvancedFeaturesPanel();
        // Set active tab to 'export' after panel opens
        setTimeout(() => {
          const tabTrigger = document.querySelector('[data-value="export"]') as HTMLElement;
          if (tabTrigger) tabTrigger.click();
        }, 100);
      }
      
      // Cmd+Shift+F (Mac) or Ctrl+Shift+F (Windows/Linux) - Freeze selected track (Requirement 15.5)
      if (modKey && e.shiftKey && e.key === 'F') {
        e.preventDefault();
        const { selectedChannelId, freezeChannel, unfreezeChannel, isChannelFrozen } = useStudioStore.getState();
        
        if (selectedChannelId) {
          const isFrozen = isChannelFrozen(selectedChannelId);
          if (isFrozen) {
            unfreezeChannel(selectedChannelId);
          } else {
            freezeChannel(selectedChannelId);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleAdvancedFeaturesPanel]);

  return (
    <div className="h-screen w-screen flex flex-col bg-zinc-900 text-white overflow-hidden">
      {/* Top Menu Bar - Hidden on mobile */}
      <div className="hidden md:block">
        <TopMenuBar />
      </div>
      
      {/* Toolbar - Responsive layout */}
      <Toolbar 
        showEffectsRack={showEffectsRack} 
        setShowEffectsRack={setShowEffectsRack}
        isMobile={isMobile}
        isTablet={isTablet}
      />
      
      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Browser Panel - Hidden by default on mobile, adjustable width on tablet */}
        <BrowserPanel isMobile={isMobile} isTablet={isTablet} />
        
        {/* Main Workspace */}
        <div className="flex-1 flex overflow-hidden">
          {/* View Content with AnimatePresence for smooth transitions */}
          <div className="flex-1 overflow-hidden relative">
            <AnimatePresence mode="wait">
              {activeView === 'pattern' && (
                <motion.div
                  key="pattern"
                  variants={viewVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute inset-0"
                >
                  <PatternSequencer />
                </motion.div>
              )}
              {activeView === 'pianoroll' && (
                <motion.div
                  key="pianoroll"
                  variants={viewVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute inset-0"
                >
                  <PianoRoll />
                </motion.div>
              )}
              {activeView === 'playlist' && (
                <motion.div
                  key="playlist"
                  variants={viewVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute inset-0"
                >
                  <Playlist />
                </motion.div>
              )}
              {activeView === 'mixer' && (
                <motion.div
                  key="mixer"
                  variants={viewVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute inset-0"
                >
                  <Mixer />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Effects Rack - Hidden on mobile and tablet */}
          {showEffectsRack && !isMobile && !isTablet && (
            <div className="w-80 border-l border-[#3a3a3a]">
              <EffectsRack />
            </div>
          )}
        </div>
      </div>
      
      {/* Transport Controls - Responsive layout */}
      <TransportControls isMobile={isMobile} isTablet={isTablet} />
      
      {/* Advanced Features Panel */}
      {showAdvancedFeaturesPanel && (
        <AdvancedFeaturesPanel onClose={closeAdvancedFeaturesPanel} />
      )}
      
      {/* Error Test Button - Remove in production */}
      {import.meta.env.DEV && <ErrorTest />}
    </div>
  );
}

export default App;
